// Selección robusta de voz (Web Speech API) por género, independiente del dispositivo.
// Las voces disponibles varían por navegador/SO, por eso usamos heurística por nombre.

const MALE = ["jorge", "diego", "juan", "carlos", "pablo", "miguel", "raul", "raúl",
  "arnau", "gonzalo", "enrique", "andres", "andrés", "male", "hombre", "masculin"];
const FEMALE = ["paulina", "monica", "mónica", "sabina", "helena", "laura", "dalia",
  "elena", "lucia", "lucía", "marisol", "esperanza", "google español", "female", "mujer", "femenin"];

// Devuelve 'hombre' | 'mujer' | 'auto' a partir del nombre de la voz.
export function voiceGender(name) {
  const n = (name || "").toLowerCase();
  if (MALE.some((w) => n.includes(w))) return "hombre";
  if (FEMALE.some((w) => n.includes(w))) return "mujer";
  return "auto";
}

// Elige la mejor voz disponible según nombre exacto (opcional) y género deseado.
export function pickVoice(voices, { vozNombre = "", vozGenero = "auto", en = false } = {}) {
  if (!voices || !voices.length) return null;
  const rx = en ? /en(-|_)?(us|gb|au)?/i : /es(-|_)?/i;
  const langVoices = voices.filter((v) => rx.test(v.lang));
  const pool = langVoices.length ? langVoices : voices;
  // 1) Nombre exacto configurado (si existe en este dispositivo)
  if (vozNombre) {
    const exact = voices.find((v) => v.name === vozNombre);
    if (exact) return exact;
  }
  // 2) Por género
  if (vozGenero === "hombre" || vozGenero === "mujer") {
    const g = pool.find((v) => voiceGender(v.name) === vozGenero);
    if (g) return g;
  }
  // 3) Predeterminada del idioma
  return pool[0] || null;
}

// ¿Hay alguna voz del género pedido en este dispositivo?
export function hasGenderVoice(voices, gender, en = false) {
  const rx = en ? /en(-|_)?/i : /es(-|_)?/i;
  return (voices || []).some((v) => rx.test(v.lang) && voiceGender(v.name) === gender);
}

// ===== Voz compartida de Jared (misma voz y configuración que el orbe asistente) =====
let cfgPromise = null;
export function loadJaredVoiceCfg() {
  if (!cfgPromise) {
    cfgPromise = fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jared/settings`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => ({ voz_nombre: d.voz_nombre || "", voz_genero: d.voz_genero || "auto", voz_rate: d.voz_rate || 1.03 }))
      .catch(() => ({ voz_nombre: "", voz_genero: "auto", voz_rate: 1.03 }));
  }
  return cfgPromise;
}

export function cleanForSpeech(text) {
  return (text || "")
    .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}\u{FE0F}\u{200D}]/gu, " ")
    .replace(/[*#_`>|]/g, " ")
    .replace(/\((?:e?s|as)\)/gi, "")
    .replace(/(\p{L})\/(\p{L})/gu, "$1 o $2")
    .replace(/[·•▪◦]/g, ", ")
    .replace(/\s*\n\s*/g, ". ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function voicesReady() {
  const s = window.speechSynthesis;
  const v = s.getVoices();
  if (v.length) return Promise.resolve(v);
  return new Promise((res) => {
    const done = () => res(s.getVoices() || []);
    s.addEventListener?.("voiceschanged", done, { once: true });
    setTimeout(done, 1200);
  });
}

export const jaredVoiceSupported = () => typeof window !== "undefined" && "speechSynthesis" in window;

// Habla con la voz del orbe. Devuelve false si el navegador no soporta voz.
export async function speakJared(text, { onStart, onEnd, volume = 1, en = false } = {}) {
  if (!jaredVoiceSupported() || !text) { onEnd?.(); return false; }
  const [cfg, voices] = await Promise.all([loadJaredVoiceCfg(), voicesReady()]);
  const s = window.speechSynthesis;
  s.cancel();
  const u = new SpeechSynthesisUtterance(cleanForSpeech(text));
  u.lang = en ? "en-US" : "es-MX";
  const v = pickVoice(voices, { vozNombre: cfg.voz_nombre, vozGenero: cfg.voz_genero, en });
  if (v) u.voice = v;
  u.rate = cfg.voz_rate || 1.03;
  u.pitch = 1.0;
  u.volume = volume;
  u.onstart = () => onStart?.();
  u.onend = () => onEnd?.();
  u.onerror = () => onEnd?.();
  try { s.resume(); } catch { /* noop */ }
  s.speak(u);
  return true;
}

export function stopJared() {
  if (jaredVoiceSupported()) window.speechSynthesis.cancel();
}
